/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { Row, Table, TableProps } from '@squiz/sds';
import clsx from 'clsx';
import { format } from 'date-fns';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MONITORING_STRINGS } from '../../Constants/Strings.constants';
import { DataTableProps } from './DataTable.props';

export const DataTable = ({
  classes,
  rows,
  columns,
  time,
  itemsPerPage = 20,
  title,
  timePeriod = 'daily',
  from,
  to,
  precedingContent,
}: DataTableProps): JSX.Element => {
  const { t } = useTranslation();
  const labels = {
    date: t(MONITORING_STRINGS.date),
    dateAndTime: t(MONITORING_STRINGS.dateAndTime),
    from: t(MONITORING_STRINGS.from),
    to: t(MONITORING_STRINGS.to),
  };

  const formatDate = useCallback(
    (date: number): string => {
      switch (timePeriod) {
        case 'hourly':
          return format(date, 'MMM dd, yyyy, h:mmaaa');
        case 'monthly':
          return format(date, 'MMM, yyyy');
        default:
          return format(date, 'MMM dd, yyyy');
      }
    },
    [timePeriod],
  );

  const subtitle = useMemo(() => {
    const isRange = from && to && !time;
    const fromString = from ? `${labels.from}: ${formatDate(from)}` : null;
    const toString = to ? `${labels.to}: ${formatDate(to)}` : null;
    const timeString = time ? `${labels.date}: ${formatDate(time)}` : null;
    if (isRange) {
      return (
        <p>
          {fromString} {toString}
        </p>
      );
    }
    if (timeString) {
      return <p>{timeString}</p>;
    }
    return <></>;
  }, [formatDate, from, labels.date, labels.from, labels.to, time, to]);

  const tableProps: TableProps = {
    isPaginated: false,
    isSlim: true,
    perPageDefault: itemsPerPage,
    tableContent: {
      columns,
      rows,
    },
  };
  return (
    <>
      {precedingContent}
      <Row classes={clsx(`monitoring-data-table`, classes)}>
        <div className="monitoring-data-table__header">
          <div className="monitoring-data-table__header-text">
            <h4>{title}</h4>
            {subtitle}
          </div>
        </div>
        <div className="monitoring-data-table__content">
          <Table {...tableProps} />
        </div>
      </Row>
    </>
  );
};
